import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ContractGenerationService } from '../../../../services/contract-generation.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { IContract } from 'src/app/domain/registration/models/IContract';
import { interval, Subscription } from 'rxjs';
import { takeWhile, switchMap } from 'rxjs/operators';
import { trustResourceUrl } from 'src/app/shared/utils/safe-html.util';

@Component({
    selector: 'app-contract-view-dialog',
    templateUrl: './contract-view-dialog.component.html',
    styleUrls: ['./contract-view-dialog.component.sass']
})
export class ContractViewDialogComponent implements OnInit, OnDestroy {
    pdfUrl: SafeResourceUrl | null = null;
    zapSignUrl: SafeResourceUrl | null = null;
    loading = true;
    error = false;
    isZapSign = false;
    zapSignError = false;
    canDownload = false;
    isPreviewMode = false;
    previewMessage = '';
    processingMessage = '';
    private pdfBlob: Blob | null = null;
    contract?: IContract;
    private autoRefreshSubscription?: Subscription;
    private isComponentActive = true;
    private readonly AUTO_REFRESH_INTERVAL = 15000; // 15 segundos

    constructor(
        public dialogRef: MatDialogRef<ContractViewDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: {
            contractId: string,
            useZapSign?: boolean,
            previewMode?: boolean,
            zapSignUrl?: string
        },
        private contractGenerationService: ContractGenerationService,
        private sanitizer: DomSanitizer,
        private toastService: ToastService,
    ) { }

    ngOnInit() {
        this.isZapSign = !!this.data.useZapSign;
        this.isPreviewMode = !!this.data.previewMode;

        if (this.data.zapSignUrl) {
            this.zapSignUrl = trustResourceUrl(this.sanitizer, this.data.zapSignUrl);
            this.loading = false;
            return;
        }

        this.refreshContractData();

        if (!this.isPreviewMode) {
            this.setupAutoRefresh();
        }
    }

    private setupAutoRefresh() {
        if (this.autoRefreshSubscription) {
            this.autoRefreshSubscription.unsubscribe();
        }

        const isFinalStatus = this.contract && ['signed', 'archived', 'canceled'].includes(this.contract.status);
        if (this.isPreviewMode || isFinalStatus) {
            return;
        }

        this.autoRefreshSubscription = interval(this.AUTO_REFRESH_INTERVAL)
            .pipe(
                takeWhile(() =>
                    this.isComponentActive &&
                    !this.isPreviewMode &&
                    !(this.contract && ['signed', 'archived', 'canceled'].includes(this.contract.status))
                ),
                switchMap(() => this.contractGenerationService.updateContractStatus(this.data.contractId))
            )
            .subscribe({
                next: (updatedContract) => {
                    if (updatedContract && ['signed', 'archived', 'canceled'].includes(updatedContract.status)) {
                        this.contract = updatedContract;
                        this.refreshContractData(false);
                        if (this.autoRefreshSubscription) {
                            this.autoRefreshSubscription.unsubscribe();
                        }
                    } else {
                        this.refreshContractData(false);
                    }
                }
            });
    }

    refreshContractData(showLoading = true) {
        if (this.contract && ['signed', 'archived', 'canceled'].includes(this.contract.status)) {
            this.loading = false;
            if (this.autoRefreshSubscription && !this.autoRefreshSubscription.closed) {
                this.autoRefreshSubscription.unsubscribe();
            }
            return;
        }

        if (showLoading) {
            this.loading = true;
        }
        this.error = false;
        this.zapSignError = false;

        this.contractGenerationService.forceRefreshStatus(this.data.contractId).subscribe({
            next: (response) => {
                this.contract = response;

                if (response.status === 'signed' || response.status === 'archived') {
                    this.canDownload = true;
                    this.loadSignedPdf();
                    return;
                }

                if (response.status !== 'signed' && response.status !== 'archived') {
                    this.isPreviewMode = true;
                    this.canDownload = false;

                    const signedCount = this.getSignedCount();
                    const totalSignatures = response.signatures?.length || 0;
                    this.previewMessage = `Visualizando versão não assinada do contrato. O download só estará disponível após todas as assinaturas. (${signedCount} de ${totalSignatures} assinaturas)`;
                }

                this.loadContract();
            },
            error: () => this.loadContract()
        });
    }

    loadContract() {
        this.loading = true;
        this.error = false;
        this.zapSignError = false;

        this.contractGenerationService.getById(this.data.contractId).subscribe({
            next: (contract: IContract) => {
                this.contract = contract;
                this.canDownload = this.canDownloadContract(contract);

                if (contract.status === 'signed' || contract.status === 'archived') {
                    this.loadSignedPdf();
                    return;
                }

                if (contract.status === 'processing') {
                    this.processingMessage = 'O documento ainda está sendo processado pela ZapSign. A visualização estará disponível em breve.';
                }

                if (this.isPreviewMode || contract.status !== 'signed') {
                    this.loadPreviewFile();
                    return;
                }

                this.loadContractFile();
            },
            error: () => {
                this.toastService.showError('Erro ao carregar informações do contrato');
                this.error = true;
                this.loading = false;
            }
        });
    }

    private canDownloadContract(contract: IContract): boolean {
        if (!contract) return false;

        if (contract.useZapSign) {
            return contract.status === 'signed' || contract.status === 'archived';
        }

        return true;
    }

    private loadPreviewFile() {
        this.loading = true;

        this.contractGenerationService.getContractPreview(this.data.contractId).subscribe({
            next: (blob: Blob) => {
                try {
                    if (!blob || blob.size === 0) {
                        throw new Error('PDF vazio ou inválido');
                    }

                    const url = URL.createObjectURL(blob);
                    this.pdfBlob = blob;
                    this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
                    this.isZapSign = false;
                    this.loading = false;
                    this.isPreviewMode = true;

                    const signedCount = this.getSignedCount();
                    const totalSignatures = this.contract?.signatures?.length || 0;
                    this.previewMessage = `Visualizando versão não assinada do contrato. O download só estará disponível após todas as assinaturas. (${signedCount} de ${totalSignatures} assinaturas)`;
                    this.canDownload = false;
                } catch (error) {
                    if (this.contract?.status === 'processing') {
                        this.processingMessage = 'O documento ainda está sendo processado. Aguarde alguns minutos e tente novamente.';
                        this.loading = false;
                    } else {
                        this.error = true;
                        this.loading = false;
                        this.toastService.showWarn('Não foi possível carregar a prévia do contrato. Tente novamente mais tarde.');
                    }
                }
            },
            error: (error: Error) => {
                if (error.message && (
                    error.message.includes('processamento') ||
                    error.message.includes('processando') ||
                    error.message.includes('ainda não está pronto') ||
                    error.message.includes('pending'))) {
                    this.processingMessage = 'O documento ainda está sendo processado. Aguarde alguns minutos e tente novamente.';
                    this.loading = false;
                } else {
                    this.error = true;
                    this.loading = false;
                    this.toastService.showWarn('Não foi possível carregar a prévia do contrato. Tentando usar visualização alternativa...');
                }
            }
        });
    }

    private loadZapSignContract() {
        if (this.contract?.status === 'signed' || this.contract?.status === 'archived') {
            this.loadSignedPdf();
            return;
        }

        if (this.contract?.status !== 'signed' && this.contract?.status !== 'archived') {
            this.loadPreviewFile();
            return;
        }

        this.contractGenerationService.getZapSignViewUrl(this.data.contractId).subscribe({
            next: (response: { url: string }) => {
                if (response?.url) {
                    const isDirectS3Url = response.url.includes('amazonaws.com') || response.url.includes('s3.amazonaws');

                    if (isDirectS3Url && this.contract?.status === 'signed') {
                        this.downloadAndDisplayPdf(response.url);
                        return;
                    }

                    this.zapSignUrl = trustResourceUrl(this.sanitizer, response.url);
                    this.loading = false;

                    if (this.contract && (this.contract.status === 'processing' || this.contract.status === 'waiting_for_signatories')) {
                        this.setupAutoRefresh();
                    }
                } else {
                    this.loadPreviewFile();
                }
            },
            error: (error: any) => {
                if (error.message && (
                    error.message.includes('processamento') ||
                    error.message.includes('processando') ||
                    error.message.includes('ainda não está pronto') ||
                    error.message.includes('pending'))) {
                    this.processingMessage = 'O documento ainda está sendo processado pela ZapSign. Aguarde alguns minutos e tente novamente.';
                    this.loading = false;
                } else {
                    this.loadPreviewFile();
                }
            }
        });
    }

    private loadSignedPdf() {
        this.loading = true;

        this.contractGenerationService.downloadContract(this.data.contractId).subscribe({
            next: (blob: Blob) => {
                try {
                    if (!blob || blob.size === 0) {
                        throw new Error('PDF vazio ou inválido');
                    }

                    const url = URL.createObjectURL(blob);
                    this.pdfBlob = blob;
                    this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
                    this.isZapSign = false;
                    this.loading = false;
                    this.canDownload = true;
                } catch (error) {
                    this.error = true;
                    this.loading = false;
                    this.toastService.showError('Erro ao carregar o contrato assinado');
                }
            },
            error: (error) => {
                if (error.message && (
                    error.message.includes('processamento') ||
                    error.message.includes('processando') ||
                    error.message.includes('ainda não está pronto') ||
                    error.message.includes('pending'))) {
                    this.processingMessage = 'O documento ainda está sendo processado. Aguarde alguns minutos e tente novamente.';
                    this.loading = false;
                } else {
                    this.error = true;
                    this.loading = false;
                    this.toastService.showError('Erro ao carregar contrato assinado');
                }
            }
        });
    }

    private downloadAndDisplayPdf(url: string) {
        this.loading = true;

        const xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.responseType = 'blob';

        xhr.onload = () => {
            if (xhr.status === 200) {
                const blob = xhr.response;
                this.pdfBlob = new Blob([blob], { type: 'application/pdf' });
                const pdfUrl = URL.createObjectURL(this.pdfBlob);
                this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(pdfUrl);
                this.isZapSign = false;
                this.loading = false;
            } else {
                this.error = true;
                this.loading = false;
                this.toastService.showError('Erro ao baixar o documento. Tente novamente.');
            }
        };

        xhr.onerror = () => {
            this.error = true;
            this.loading = false;
            this.toastService.showError('Erro de conexão ao baixar o documento. Verifique sua conexão e tente novamente.');
        };

        xhr.send();
    }

    private loadContractFile() {
        this.contractGenerationService.getContractFile(this.data.contractId).subscribe({
            next: (response: Blob) => {
                if (!response || response.size === 0) {
                    if (this.isPreviewMode && this.contract?.status !== 'signed') {
                        this.processingMessage = 'O documento ainda está em processamento. Aguarde alguns minutos e tente novamente.';
                        this.setupAutoRefresh();
                    } else {
                        this.toastService.showError('Falha ao carregar o contrato: arquivo vazio.');
                    }
                    this.loading = false;
                    return;
                }

                try {
                    this.pdfBlob = new Blob([response], { type: 'application/pdf' });
                    const pdfUrl = URL.createObjectURL(this.pdfBlob);
                    this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(pdfUrl);

                    this.verifyPdfSignature(this.pdfBlob).then(isValid => {
                        if (!isValid) {
                            if (this.isPreviewMode && this.contract?.status !== 'signed') {
                                this.processingMessage = 'O documento ainda está em processamento. Aguarde a finalização para visualizar.';
                                this.setupAutoRefresh();
                            } else {
                                this.toastService.showError('O arquivo do contrato está corrompido.');
                            }
                        }
                        this.loading = false;
                    });
                } catch (e) {
                    if (this.isPreviewMode && this.contract?.status !== 'signed') {
                        this.processingMessage = 'O documento ainda está sendo processado. A página será atualizada automaticamente.';
                        this.setupAutoRefresh();
                    } else {
                        this.toastService.showError('Erro ao processar o documento PDF.');
                    }
                    this.loading = false;
                }
            },
            error: (error: Error) => {
                if (error.message && (
                    error.message.includes('processamento') ||
                    error.message.includes('processando') ||
                    error.message.includes('ainda não está pronto') ||
                    error.message.includes('pending'))) {
                    this.processingMessage = 'O documento ainda está em processamento. Aguarde alguns minutos e tente novamente.';
                    this.loading = false;
                    this.setupAutoRefresh();
                    return;
                }

                let errorMessage = 'Erro ao carregar o contrato.';
                if (error.message.includes('404')) {
                    errorMessage = 'Contrato não encontrado.';
                } else if (error.message.includes('500')) {
                    errorMessage = 'Erro no servidor ao carregar o contrato.';
                }

                this.toastService.showError(errorMessage);
                this.error = true;
                this.loading = false;
            }
        });
    }

    private verifyPdfSignature(blob: Blob): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if (this.isPreviewMode) {
                resolve(true);
                return;
            }

            const reader = new FileReader();
            reader.onload = () => {
                const buffer = reader.result as ArrayBuffer;

                if (!buffer || buffer.byteLength < 100) {
                    resolve(false);
                    return;
                }

                const bytes = new Uint8Array(buffer);
                const pdfSignature = [0x25, 0x50, 0x44, 0x46, 0x2D];
                let isPdf = true;

                for (let i = 0; i < pdfSignature.length; i++) {
                    if (bytes[i] !== pdfSignature[i]) {
                        isPdf = false;
                        break;
                    }
                }

                resolve(isPdf);
            };

            reader.onerror = () => resolve(false);
            reader.readAsArrayBuffer(blob);
        });
    }

    downloadContract() {
        if (this.loading || this.error) return;

        this.loading = true;
        this.contractGenerationService.downloadContract(this.data.contractId).subscribe({
            next: (blob: Blob) => {
                try {
                    if (!blob || blob.size === 0) {
                        throw new Error('PDF vazio ou inválido');
                    }

                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `Contrato_${this.data.contractId}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                    this.toastService.showSuccess('Download iniciado com sucesso');
                } catch (error) {
                    this.toastService.showError('Erro ao processar o download do contrato');
                } finally {
                    this.loading = false;
                }
            },
            error: () => {
                this.toastService.showError('Erro ao baixar contrato. Tente novamente.');
                this.loading = false;
            }
        });
    }

    close() {
        this.dialogRef.close();
    }

    ngOnDestroy() {
        this.isComponentActive = false;
        if (this.autoRefreshSubscription) {
            this.autoRefreshSubscription.unsubscribe();
        }

        if (this.pdfUrl) {
            const urlString = this.pdfUrl.toString();
            if (urlString.startsWith('blob:')) {
                try {
                    URL.revokeObjectURL(urlString);
                } catch (error) {
                }
            }
        }
    }

    getSignedCount(): number {
        if (!this.contract?.signatures?.length) {
            return 0;
        }
        return this.contract.signatures.filter(s => s.status === 'signed').length;
    }

    scrollToBottom(): void {
        try {
            const containerElement = document.querySelector('.viewer-content');
            if (containerElement) {
                containerElement.scrollTo({
                    top: containerElement.scrollHeight,
                    behavior: 'smooth'
                });
            }
        } catch (error) {
        }
    }
}